import { useQuery } from '@tanstack/react-query'
import { api } from '../../utils/api'
import type { FloorPlanning, Placement } from '../../utils/placement.logic'
import { rotatedRectBounds } from '../../utils/floorPlan.logic'
import { placeCalculatorLabels } from '../../utils/calculatorLabels.logic'

type FloorRack = Placement & { id: number; label: string }

export function FloorPlanningLayer({ planning, racks, ppm, zoom, onSelect, targetLabel, labels, labelAnchor, viewport }: {
  planning: FloorPlanning; racks: FloorRack[]; ppm: number; zoom: number;
  onSelect?: (id: number) => void; targetLabel?: (rowId: string, position: number) => string;
  labels?: Record<string, string>; labelAnchor?: (xM: number) => 'start' | 'end';
  viewport?: { width: number; height: number; offsetX: number; offsetY: number };
}) {
  const byId = new Map(racks.map(rack => [rack.id, rack]))
  const targets = planning.placements.flatMap(plan => {
    const rack = byId.get(plan.rack_id)
    if (!rack) return []
    const target = plan.target, width = rack.widthM * ppm, depth = rack.depthM * ppm
    const label = target.row_id && target.rack_pos_in_row && targetLabel
      ? targetLabel(target.row_id, target.rack_pos_in_row) : labels?.[String(rack.id)] ?? (target.row_id ? `Место ${target.rack_pos_in_row}` : 'Вне ряда')
    return [{ plan, rack, target, width, depth, label, x: target.x_m * ppm, y: target.y_m * ppm }]
  })
  const visibleViewport = viewport && viewport.width > 0 && viewport.height > 0 ? viewport : undefined
  const labelPositions = new Map<number, { x: number; y: number; leader?: [number, number, number, number] }>()
  if (visibleViewport) {
    const screenBox = (rect: { x: number; y: number; w: number; h: number; rotation: number }) => {
      const bounds = rotatedRectBounds(rect)
      return { x: visibleViewport.offsetX + bounds.minX * zoom, y: visibleViewport.offsetY + bounds.minY * zoom,
        w: (bounds.maxX - bounds.minX) * zoom, h: (bounds.maxY - bounds.minY) * zoom }
    }
    // ponytail: visible-label placement is quadratic; use a spatial index if dense halls make it slow.
    const candidates = targets.map(item => ({ ...screenBox({ x: item.x, y: item.y, w: item.width, h: item.depth, rotation: item.target.rotation }),
      id: item.rack.id, labelWidth: (`План: ${item.label}`).length * 8 + 8, labelHeight: 18 }))
      .filter(box => box.x + box.w >= 0 && box.y + box.h >= 0 && box.x <= visibleViewport.width && box.y <= visibleViewport.height)
    const occupied = racks.map(rack => screenBox({ x: rack.xM * ppm, y: rack.yM * ppm,
      w: rack.widthM * ppm, h: rack.depthM * ppm, rotation: rack.orientation }))
      .filter(box => box.x + box.w >= 0 && box.y + box.h >= 0 && box.x <= visibleViewport.width && box.y <= visibleViewport.height)
    const placed = placeCalculatorLabels(candidates, [], { x: 8, y: 8,
      w: Math.max(0, visibleViewport.width - 16), h: Math.max(0, visibleViewport.height - 16) }, occupied)
    for (const label of placed) {
      const toX = (value: number) => (value - visibleViewport.offsetX) / zoom
      const toY = (value: number) => (value - visibleViewport.offsetY) / zoom
      labelPositions.set(label.id, {
        x: toX(label.x + label.w / 2), y: toY(label.y + label.h / 2),
        leader: label.external ? [toX(label.anchorX), toY(label.anchorY),
          toX(Math.max(label.x, Math.min(label.x + label.w, label.anchorX))),
          toY(Math.max(label.y, Math.min(label.y + label.h, label.anchorY)))] : undefined,
      })
    }
  }
  return <g aria-label="Планируемые размещения" data-plan-layer="planning">
    {targets.map(({ plan, rack, target, width, depth, label, x, y }) => {
      const anchor = labelAnchor?.(target.x_m) ?? 'start'
      const position = labelPositions.get(rack.id)
      return <g key={plan.rack_id} role="button" tabIndex={0} aria-label={`План переноса: ${rack.label}`} style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        onClick={() => onSelect?.(rack.id)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect?.(rack.id) } }}>
        <title>{rack.label} → {label}; X {target.x_m} м, Y {target.y_m} м</title>
        <line x1={(rack.xM + rack.widthM / 2) * ppm} y1={(rack.yM + rack.depthM / 2) * ppm}
          x2={x + width / 2} y2={y + depth / 2} stroke="#a3a3a3" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        <rect x={x} y={y} width={width} height={depth} transform={`rotate(${target.rotation},${x + width / 2},${y + depth / 2})`}
          fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
        {position?.leader && <line x1={position.leader[0]} y1={position.leader[1]} x2={position.leader[2]} y2={position.leader[3]}
          stroke="#a3a3a3" vectorEffect="non-scaling-stroke" />}
        {(!visibleViewport || position) && <text x={position?.x ?? x + (anchor === 'end' ? width : 0)}
          y={position?.y ?? y - 6 / zoom} textAnchor={position ? 'middle' : anchor} dominantBaseline={position ? 'central' : undefined}
          fontSize={12 / zoom} fill="#fbbf24" paintOrder="stroke" stroke="#111827" strokeWidth={3 / zoom}>План: {label}</text>}
      </g>
    })}
  </g>
}

type Reservation = { id: string; kind: 'row_position' | 'rack' | 'u_range'; rack_id?: number; row_id?: string; position?: number; u_start?: number; u_end?: number; label: string; reason?: string }

export function FloorReservationsList({ code, planning, rackLabels }: {
  code: string; planning: FloorPlanning; rackLabels: Record<number, string>;
}) {
  const { data, isError, isLoading } = useQuery<Reservation[]>({ queryKey: ['floor-reservations', code],
    queryFn: () => api.get(`/api/datacenters/${code}/floor-reservations`).then(result => result.data), enabled: !!code,
  })
  const records = (Array.isArray(data) ? data : []).filter(item => item.kind !== 'rack')
  records.push(...planning.rack_reservations.map(item => ({ ...item, kind: 'rack' as const,
    label: rackLabels[item.rack_id] || `Стойка #${item.rack_id}` })))
  return <aside aria-label="Резервы зала" style={{ position: 'absolute', right: 12, top: 64, maxWidth: 'calc(100% - 24px)', maxHeight: '35%', overflow: 'auto', padding: 8,
    background: 'var(--bg-panel, #111827)', border: '1px solid var(--border, #374151)', borderRadius: 4, fontSize: 12, zIndex: 5 }}>
    <strong>Резервы</strong>
    {isLoading ? <div role="status">Загрузка резервов...</div> : isError ? <div role="alert">Не удалось загрузить резервы</div> : !records.length && <div>Резервов нет</div>}
    {records.map(item => <div key={`${item.kind}:${item.id}`} style={{ marginTop: 6 }}>
      <a href={item.rack_id ? `/${encodeURIComponent(code)}/racks/${item.rack_id}` : `/${encodeURIComponent(code)}/floor-plan?row=${encodeURIComponent(item.row_id ?? '')}`}>
        {item.kind === 'rack' ? 'Вся стойка' : item.kind === 'u_range' ? `U${item.u_start}–U${item.u_end}` : `Место ${item.position}`} · {item.label}
      </a>{item.reason && <div>{item.reason}</div>}
    </div>)}
  </aside>
}
